import { Injectable, signal } from '@angular/core';
import { ScheduleOutageService } from '../admin/schedule-outage/schedule-outage.service';
import { MatDialog } from '@angular/material/dialog';
import { MaintenanceDialogComponent } from '../../components/shared/maintenance-dialog/maintenance-dialog.component';
import { ScheduleOutageResponse } from '../../model/admin/schedule-outage.model';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class MaintenanceDialogService {
  startDateTime:Date = null;
  startDateAndTimeFormatted:string = null
  endDateTime = null;
  endDateAndTimeFormatted:string = null;

  scheduleMaintenanceNotification = signal(false)

  constructor(
    private scheduleOutageService: ScheduleOutageService, 
    private dialog: MatDialog, 
    private datePipe: DatePipe,
    private router: Router
  ) {}

  async checkScheduleOutage():Promise<boolean>{
    this.scheduleMaintenanceNotification.set(false);
    try {
      const isHiddenByAdmin = sessionStorage.getItem('scheduleOutageDialogHiddenByAdmin') === 'Y';
      const schedule_outage = await firstValueFrom(this.scheduleOutageService.getScheduleOutage());

      if (!schedule_outage || isHiddenByAdmin) return false;

      this.setStartEndDateTime(schedule_outage);

      if (this.isCurrentTimeInScheduleOutageRange()) {
        this.showMaintenanceDialog(schedule_outage);
        return true;
      }

      if (this.isCurrentTimeInNotificationRange(schedule_outage)) {
        this.scheduleMaintenanceNotification.set(true);
      }

      if (this.isCurrentTimeOutOfScheduleOutageRange()) {
        const outage_data = {
          is_active: false,
          updated_by:Number(sessionStorage.getItem('userId'))
        }
        this.scheduleOutageService.deleteScheduleOutage(schedule_outage.outage_id, outage_data).subscribe((res)=>{});
      }

      return false;
    } catch(error){
      return false
    }
  }

  showMaintenanceDialog(schedule_outage:ScheduleOutageResponse){
    const isAdminUser = sessionStorage.getItem("role") == 'SUPERVISOR';
    this.dialog.open(MaintenanceDialogComponent,{
      width: `94%`,
      height: `80%`,
      panelClass:'custom-dialog-container',
      disableClose: ! isAdminUser,
      data:{isAdminUser}
    });

    sessionStorage.clear();
    this.router.navigate(['/app-home'], { skipLocationChange: true });
    if (isAdminUser) {
      sessionStorage.setItem('role', 'SUPERVISOR')
    }
  }

  isCurrentTimeInNotificationRange(schedule_outage:ScheduleOutageResponse) {
    const currentDateTime = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const diffMs = this.startDateTime.getTime() - currentDateTime.getTime(); // difference in milliseconds
    const diffHours = diffMs / (1000 * 60 * 60); // convert to hours
    this.startScheduleOutageScheduler(diffMs);
    return diffHours > 0 && diffHours <= schedule_outage.outage_first_notify_before_hours;
  }

  startScheduleOutageScheduler(timeInMs: number) {
    if(timeInMs > 0) {
      setTimeout(() => {
        sessionStorage.clear();
        this.router.navigate(['/app-home'], { skipLocationChange: true });
      }, timeInMs);
    }
  }

  isCurrentTimeInScheduleOutageRange() {
    const currentDateTime = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
    return currentDateTime >= this.startDateTime && currentDateTime <= this.endDateTime;
  }

  isCurrentTimeOutOfScheduleOutageRange() {
    const currentDateTime = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
    return currentDateTime >= this.endDateTime;
  }

  setStartEndDateTime(schedule_outage:ScheduleOutageResponse){
    const startDateTimeStr = `${schedule_outage.outage_start_date}T${schedule_outage.outage_start_time}`;
    const endDateTimeStr = `${schedule_outage.outage_end_date}T${schedule_outage.outage_end_time}`;
    this.startDateTime = new Date(startDateTimeStr);
    this.endDateTime = new Date(endDateTimeStr);
    this.setStartEndDateTimeformat(schedule_outage)
  }

  setStartEndDateTimeformat(schedule_outage:ScheduleOutageResponse){
    this.startDateAndTimeFormatted = this.datePipe.transform(this.startDateTime, 'MMM dd, yyyy, hh:mm a');
    if (schedule_outage.outage_start_date == schedule_outage.outage_end_date) {
      this.endDateAndTimeFormatted = this.datePipe.transform(this.endDateTime, 'hh:mm a');
    } else {
      this.endDateAndTimeFormatted = this.datePipe.transform(this.endDateTime, 'MMM dd, yyyy, hh:mm a');
    }
  }

  getStartDateTimeToEndDateTimeText(){
    return `${this.startDateAndTimeFormatted} EST to ${this.endDateAndTimeFormatted} EST`
  }
}
